import { loadStripe, Stripe } from '@stripe/stripe-js';

// Chiave pubblica Stripe
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RnkA8RUcr7bTSlQbG2GOWEd6IdlCesVaqtgGihLg7VoDkKZTDS2IyMZ3L3mFGljaHUMnZw3BBmh79nVn1puLYG700lIZrwben';

// Istanza Stripe
let stripePromise: Promise<Stripe | null>;

export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

// Configurazione per i pagamenti
export const STRIPE_CONFIG = {
  currency: 'eur',
  country: 'IT',
  appearance: {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: '#0d9488',
      colorBackground: '#ffffff',
      colorText: '#30313d',
      colorDanger: '#df1b41',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px'
    }
  }
};

// Tipi per i pagamenti
export interface PaymentIntentData {
  amount: number;
  currency: string;
  bookingId: string;
  userId: string;
  metadata: {
    accommodationId: string;
    checkIn: string;
    checkOut: string;
    guestName: string;
    guestEmail: string;
  };
}

export interface PaymentResult {
  success: boolean;
  paymentIntentId?: string;
  clientSecret?: string;
  error?: string;
}

// Funzione per creare un Payment Intent
export const createPaymentIntent = async (data: PaymentIntentData): Promise<PaymentResult> => {
  try {
    // Qui dovresti chiamare il tuo backend per creare il Payment Intent
    // Per ora simulo la risposta
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error('Errore nella creazione del pagamento');
    }

    const result = await response.json();
    
    return {
      success: true,
      paymentIntentId: result.paymentIntentId,
      clientSecret: result.clientSecret
    };
  } catch (error) {
    console.error('Errore Payment Intent:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Errore sconosciuto'
    };
  }
};

// Funzione per confermare il pagamento
export const confirmPayment = async (
  stripe: Stripe,
  clientSecret: string,
  elements: any,
  paymentData: {
    name: string;
    email: string;
    phone?: string;
  }
): Promise<PaymentResult> => {
  try {
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/booking-confirmation`,
        payment_method_data: {
          billing_details: {
            name: paymentData.name,
            email: paymentData.email,
            phone: paymentData.phone
          }
        }
      },
      redirect: 'if_required'
    });

    if (error) {
      return {
        success: false,
        error: error.message || 'Errore nel pagamento'
      };
    }

    if (paymentIntent?.status === 'succeeded') {
      return {
        success: true,
        paymentIntentId: paymentIntent.id
      };
    }

    return {
      success: false,
      error: 'Pagamento non completato'
    };
  } catch (error) {
    console.error('Errore conferma pagamento:', error);
    return {
      success: false,
      error: 'Errore nella conferma del pagamento'
    };
  }
};

// Funzione per calcolare i costi di una prenotazione
export const calculateBookingCosts = (
  basePrice: number,
  nights: number,
  options: {
    cleaningFeePercentage?: number;
    serviceFeePercentage?: number;
    taxPercentage?: number;
  } = {}
) => {
  const {
    cleaningFeePercentage = 0.1, // 10%
    serviceFeePercentage = 0.05, // 5%
    taxPercentage = 0.1 // 10%
  } = options;

  const subtotal = basePrice * nights;
  const cleaningFee = Math.round(subtotal * cleaningFeePercentage * 100) / 100;
  const serviceFee = Math.round(subtotal * serviceFeePercentage * 100) / 100;
  const taxes = Math.round(subtotal * taxPercentage * 100) / 100;
  const total = subtotal + cleaningFee + serviceFee + taxes;

  return {
    subtotal,
    cleaningFee,
    serviceFee,
    taxes,
    total,
    nights
  };
};

// Utilità per formattare i prezzi
export const formatPrice = (amount: number, currency: string = 'EUR'): string => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

// Funzione per validare i dati della carta
export const validateCardData = (cardData: {
  cardNumber: string;
  expiryDate: string;
  cvc: string;
  cardholderName: string;
}): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Valida numero carta (basic check)
  if (!cardData.cardNumber || cardData.cardNumber.replace(/\s/g, '').length < 13) {
    errors.push('Numero carta non valido');
  }

  // Valida data scadenza
  if (!cardData.expiryDate || !/^\d{2}\/\d{2}$/.test(cardData.expiryDate)) {
    errors.push('Data di scadenza non valida (MM/YY)');
  } else {
    const [month, year] = cardData.expiryDate.split('/');
    const currentYear = new Date().getFullYear() % 100;
    const currentMonth = new Date().getMonth() + 1;
    
    if (parseInt(month) < 1 || parseInt(month) > 12) {
      errors.push('Mese non valido');
    }
    
    if (parseInt(year) < currentYear || (parseInt(year) === currentYear && parseInt(month) < currentMonth)) {
      errors.push('Carta scaduta');
    }
  }

  // Valida CVC
  if (!cardData.cvc || cardData.cvc.length < 3 || cardData.cvc.length > 4) {
    errors.push('CVC non valido');
  }

  // Valida nome
  if (!cardData.cardholderName || cardData.cardholderName.trim().length < 2) {
    errors.push('Nome del titolare richiesto');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Configurazione webhook Stripe (per il backend)
export const STRIPE_WEBHOOK_EVENTS = {
  PAYMENT_INTENT_SUCCEEDED: 'payment_intent.succeeded',
  PAYMENT_INTENT_PAYMENT_FAILED: 'payment_intent.payment_failed',
  CHARGE_DISPUTE_CREATED: 'charge.dispute.created'
};

// Mock della funzione per il backend (da implementare lato server)
export const mockBackendPaymentIntent = (data: PaymentIntentData) => {
  // Questa funzione simula quello che faresti nel backend
  return {
    paymentIntentId: `pi_mock_${Date.now()}`,
    clientSecret: `pi_mock_${Date.now()}_secret_mock`,
    amount: data.amount,
    currency: data.currency,
    status: 'requires_payment_method'
  };
}; 